import { useCallback, useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { areaOf, navFor, sectionOf, SETTINGS } from "@/components/shell/navItems";
import { useUi } from "@/stores/ui";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { useUser } from "@/lib/queries";

/**
 * Booking detail lives in the URL (?booking=B-4468) so it opens over any
 * screen, survives a reload, and can be shared.
 */
export function useOpenBooking() {
  const navigate = useNavigate();
  return (code: string | null) =>
    void navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => ({ ...prev, booking: code ?? undefined })) as never,
    });
}

/** "02" — this screen's area number in the course bar, for the page eyebrow. */
export function useSectionIndex(): string | undefined {
  const me = useUser();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const area = areaOf(path);
  const i = navFor(me.role).findIndex((a) => a.id === area);
  return i >= 0 ? String(i + 1).padStart(2, "0") : undefined;
}

/** Open quick entry prefilled — from a calendar cell, a search result, or Today. */
export function useStartBooking() {
  const navigate = useNavigate();
  return (
    p: { propertyId?: string; roomId?: string; bedId?: string; checkIn?: string; checkOut?: string; adults?: number; step?: number } = {},
  ) => void navigate({ to: "/new", search: p as never });
}

/** Each area remembers the section it was last on, for this session. */
export function useRememberSection() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const role = useUser().role;
  const remember = useUi((s) => s.rememberSection);
  useEffect(() => {
    // Settings isn't an area, but its layout still reopens on the last section.
    const setting = sectionOf(
      SETTINGS.filter((s) => s.visible(role)),
      path,
    );
    if (setting) return remember("settings", setting.to);
    const id = areaOf(path);
    const area = navFor(role).find((a) => a.id === id);
    if (!area) return;
    const hit = sectionOf(area.sections ?? [], path);
    if (hit) remember(area.id, hit.to);
  }, [path, role, remember]);
}

/** Folded to icons: the person's choice, or — until they make one — whether the screen is under 1100px. */
export function useRailCollapsed(): [boolean, () => void] {
  const chosen = useUi((s) => s.railCollapsed);
  const set = useUi((s) => s.setRailCollapsed);
  const narrow = useMediaQuery("(max-width: 1099px)");
  const collapsed = chosen ?? narrow;
  const toggle = useCallback(() => set(!collapsed), [set, collapsed]);
  return [collapsed, toggle];
}
